import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Loader2, Star, FileUp, FileText, Trash2 } from 'lucide-react';
import api from '../api/client';
import { NSF_OPTIONS } from '../constants/nsf';
import ComboboxCreate from './ComboboxCreate';
import AddressAutocomplete, { formatAddress } from './AddressAutocomplete';
import { toast } from '../hooks/use-toast';

const LEVELS = ['Débutant', 'Intermédiaire', 'Avancé', 'Tout public'];
const MODES = ['Présentiel', 'Distanciel', 'Mixte'];

const EMPTY = {
  title: '', category: '', nsf_code: '', level: 'Débutant', duration: 0, price: 0,
  mode: 'Présentiel', trainer: '', public_cible: '',
  description: '', objectives: '', prerequisites: '', programme_pdf_url: '',
  // **Ou le programme se donne.** '' = non precise : il garde alors son
  // adresse a lui (une salle louee), ce qui est le comportement d'avant.
  lieu_defaut: '',
  // Structured address — saisie seulement quand aucun repere n'est pose
  street: '', postal_code: '', city: '',
  // Legacy
  location: '',
  // `sessions` et `rating` ont ete retires le 2026-11-02 : les deux valaient 0
  // sur les quarante-deux fiches, et aucun ecran ne les lisait. Le vrai compte
  // de sessions vient de `/analytics/programs-session-counts`, la vraie note de
  // `/analytics/programs-ratings` — calculee depuis les evaluations, et c'est
  // elle que cette fiche affiche. Deux champs qui ne pouvaient que contredire
  // un chiffre juste. Les colonnes restent en base, plus personne ne les ecrit.
  color: '#4F46E5',
  // Comment les acquis se valident. Vide : le programme ne declare rien, et
  // c'est le quiz qui decide s'il y en a un — les trente-trois programmes
  // logiciels n'ont rien a remplir.
  // `decide_acquis` a disparu le 2026-11-02 : les deux outils comptent
  // ensemble, plus aucun n'arbitre. La colonne reste en base sur les 37
  // fiches qui la portaient, et plus personne ne la lit.
  validation_acquis: '',
  criteres_acquis: [],
  // **Le déroulé de la formation.** Le document PDF lisait `chapters` depuis le
  // premier jour et aucun écran ne l'écrivait : zéro fiche sur quarante-deux en
  // portait un, donc la section « Programme détaillé » ne s'est jamais
  // imprimée. On garde le nom que le document lit — on ne renomme pas ce qui
  // est imprimé.
  chapters: [],
  // Ce que la fiche programme publie, et que le document imprime. Les trois
  // noms anglais sont ceux que la fiche PDF lit depuis le premier jour : on ne
  // renomme pas ce qui est imprime. `modalites_evaluation` a disparu — c'etait
  // un second nom pour `evaluation_methods`, tape a l'ecran et lu par personne.
  pedagogical_methods: '', evaluation_methods: '', accessibility: '',
  moyens_pedagogiques: '', qualification_formateur: '', textes_officiels: '',
  type_parcours: '', prix_unite: '', delai_acces: '', sanction: '', validite: '',
  effectif_min: 0, effectif_max: 0,
};

// Les rubriques que Qualiopi attend sur une fiche programme. Rien n'est bloque
// — on ne refuse pas d'enregistrer un programme incomplet — mais on le dit la
// ou ca se corrige : sans elles, le document imprime un texte tout fait, et
// c'est exactement ce qu'un audit releve.
const ATTENDUS = {
  pedagogical_methods: 'Sans elle, le document imprime un texte générique.',
  evaluation_methods: "C'est ce qu'un auditeur lit en premier, et ça ne se dit pas pour toute la maison.",
  accessibility: 'Indicateur 26. Sans elle, le document imprime la phrase générique.',
  sanction: "Ce que l'apprenant obtient : attestation, certificat, titre.",
  delai_acces: 'Indicateur 4 : le délai entre la demande et le début.',
  qualification_formateur: 'Indicateur 21 : ce qui rend le formateur compétent sur ce sujet.',
  moyens_pedagogiques: 'Indicateur 17 : les moyens techniques mis à disposition.',
  effectif_max: 'Combien de personnes au maximum par session.',
};

export default function ProgrammeFormDialog({
  open,
  onOpenChange,
  mode, // 'create' | 'edit'
  initialValues,
  categories, // [{id,name}] from parent
  onCreated,
  onUpdated,
  realRating, // { avg, count } computed from evaluations (edit mode)
}) {
  const isEdit = mode === 'edit';
  const [values, setValues] = useState(EMPTY);
  const [trainers, setTrainers] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  // Ce que l'organisme dit une fois pour toutes ses fiches. Rien n'est
  // recopie ici : une fiche muette le reprend a l'impression, et taper dans
  // le champ la decroche. C'est ce qui permet de corriger la phrase
  // d'accessibilite une fois au lieu de rouvrir quarante et une fiches.
  const [communes, setCommunes] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load trainers + target audiences on open
  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get('/trainers').then((r) => r.data).catch(() => []),
      api.get('/target-audiences').then((r) => r.data.items || []).catch(() => []),
      // Ce qui a deja ete tape sur les autres fiches : « Initiale », « 15 jours
      // a 1 mois », « par personne ». La liste propose et apprend, elle
      // n'impose rien — un champ libre se tape toujours.
      api.get('/programmes/valeurs-utilisees').then((r) => r.data.valeurs || {}).catch(() => ({})),
      api.get('/orgs/qualiopi-templates').then((r) => r.data || {}).catch(() => ({})),
    ]).then(([t, a, v, c]) => {
      setTrainers(t); setAudiences(a); setSuggestions(v); setCommunes(c);
    });
  }, [open]);

  // Initialize from existing data — also split legacy "location" string if needed
  useEffect(() => {
    if (!open) return;
    const base = { ...EMPTY, ...(initialValues || {}) };
    // If structured fields are empty but legacy location exists, try to parse
    if (!base.lieu_defaut && !base.street && !base.city && base.location) {
      // Simple parse: "12 rue X, 75011 Paris" → street="12 rue X", postal="75011", city="Paris"
      const parts = String(base.location).split(',').map((p) => p.trim());
      if (parts.length >= 2) {
        base.street = parts[0];
        const tail = parts.slice(1).join(' ');
        const m = tail.match(/^(\d{4,5})\s+(.+)$/);
        if (m) { base.postal_code = m[1]; base.city = m[2]; }
        else { base.city = tail; }
      } else {
        base.street = base.location;
      }
    }
    setValues(base);
    setError(null);
  }, [open, initialValues]);

  const trainerOptions = useMemo(() => (trainers || []).map((t) => t.name).filter(Boolean), [trainers]);

  const setVal = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const handleAudienceChange = async (newValue, updatedList) => {
    setVal('public_cible', newValue);
    // Persist new entry to backend if list grew
    if (updatedList.length > audiences.length) {
      setAudiences(updatedList);
      try { await api.put('/target-audiences', { items: updatedList }); } catch {/* silent */ }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      // Rebuild flat "location" for backwards compat + keep structured fields
      const payload = {
        ...values,
        price: Number(values.price) || 0,
        duration: Number(values.duration) || 0,
        effectif_min: Number(values.effectif_min) || 0,
        effectif_max: Number(values.effectif_max) || 0,
        ...(values.lieu_defaut
          ? { street: '', postal_code: '', city: '', location: '' }
          : { location: formatAddress({ street: values.street, postal_code: values.postal_code, city: values.city }) || values.location || '' }),
        color: values.color || '#4F46E5',
      };
      if (isEdit) {
        const { data } = await api.put(`/programs/${initialValues.id}`, payload);
        onUpdated && onUpdated(data);
        toast({ title: 'Programme modifié', description: data.title });
      } else {
        const { data } = await api.post('/programs', payload);
        onCreated && onCreated(data);
        toast({ title: 'Programme créé', description: data.title });
      }
      onOpenChange(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[780px] rounded-2xl p-0 overflow-hidden">
        <div className="max-h-[88vh] overflow-y-auto">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#EFF0F6] bg-white sticky top-0 z-10">
            <DialogTitle className="text-[18px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {isEdit ? 'Modifier le programme' : 'Nouveau programme'}
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              Tout ce que la fiche programme publie : pédagogie, conditions particulières, sanction. C’est ce document que le client lit et qu’un auditeur demande.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
            {/* Identité */}
            <section className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Titre" required full>
                <input required data-testid="prog-field-title" value={values.title} onChange={(e) => setVal('title', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Catégorie" required>
                <select required value={values.category} onChange={(e) => setVal('category', e.target.value)} className={inputCls}>
                  <option value="">Sélectionner…</option>
                  {(categories || []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Spécialité NSF">
                <select value={values.nsf_code || ''} onChange={(e) => setVal('nsf_code', e.target.value)} className={inputCls}>
                  <option value="">— Choisir une spécialité NSF —</option>
                  {NSF_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </Field>
              <Field label="Niveau" required>
                <select required value={values.level} onChange={(e) => setVal('level', e.target.value)} className={inputCls}>
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Durée (heures)" required>
                <input required type="number" value={values.duration} onChange={(e) => setVal('duration', Number(e.target.value))} className={inputCls} />
              </Field>
              <Field label="Type de parcours">
                <Suggere
                  nom="type_parcours"
                  value={values.type_parcours}
                  options={suggestions.type_parcours}
                  onChange={(v) => setVal('type_parcours', v)}
                  placeholder="Initiale, Recyclage, MAC…"
                />
                <Aide>Une « Initiale » et un « Recyclage » ne sont pas la même formation.</Aide>
              </Field>
              <Field label="Prix (€ HT)" required>
                <input required type="number" value={values.price} onChange={(e) => setVal('price', Number(e.target.value))} className={inputCls} />
              </Field>
              <Field label="Le prix s'entend">
                <Suggere
                  nom="prix_unite"
                  value={values.prix_unite}
                  options={suggestions.prix_unite}
                  onChange={(v) => setVal('prix_unite', v)}
                  placeholder={communes.prix_unite || "par personne, par groupe…"}
                />
                <Aide>Un tarif sans son unité se lit comme un tarif de groupe une fois sur deux.</Aide>
              </Field>
              <Field label="Mode par défaut">
                <select value={values.mode} onChange={(e) => setVal('mode', e.target.value)} className={inputCls}>
                  {MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
              <Field label="Formateur par défaut">
                <ComboboxCreate
                  testId="prog-trainer-select"
                  value={values.trainer}
                  options={trainerOptions}
                  onChange={(v) => setVal('trainer', v)}
                  placeholder={trainerOptions.length ? '— Choisir un formateur —' : 'Aucun formateur — entrez un nom'}
                />
                <div className="text-[10.5px] text-[#8E8FA5] mt-1">Liste issue de /Formateurs. Vous pouvez aussi saisir un nom libre.</div>
              </Field>
              <Field label="Public cible" full>
                <ComboboxCreate
                  testId="prog-audience-select"
                  value={values.public_cible}
                  options={audiences}
                  onChange={handleAudienceChange}
                  placeholder="— Choisir ou créer —"
                />
                <div className="text-[10.5px] text-[#8E8FA5] mt-1">Saisissez pour filtrer. Tapez un nouveau public et cliquez « Créer » — il sera mémorisé pour toute l'équipe.</div>
              </Field>
            </section>

            {/* **Ou la formation se donne — un repere, pas une adresse.**
                Vingt-sept programmes portaient l'adresse de la maison, tapee
                fiche par fiche, en quatre orthographes ; chaque session en
                recopiait la chaine. « Dans nos locaux » renvoie desormais aux
                reglages : une seule adresse, corrigee une fois. */}
            <section className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-4">
              <h3 className="text-[13px] font-semibold text-[#1A1B2E] uppercase tracking-wide mb-3">Lieu par défaut</h3>
              <div className="flex flex-wrap gap-2 mb-1">
                {[
                  { cle: 'NOS_LOCAUX', titre: 'Dans nos locaux', aide: "l'adresse des réglages" },
                  { cle: 'EN_ENTREPRISE', titre: 'En entreprise', aide: 'chez le client' },
                  { cle: '', titre: 'Autre adresse', aide: 'une salle, à saisir' },
                ].map((o) => {
                  const choisi = (values.lieu_defaut || '') === o.cle;
                  return (
                    <button
                      key={o.cle || 'autre'}
                      type="button"
                      data-testid={`prog-lieu-${o.cle || 'autre'}`}
                      onClick={() => setVal('lieu_defaut', o.cle)}
                      className={`text-left px-3 py-2 rounded-xl border text-[12.5px] ${choisi ? 'border-[#4F46E5] bg-white ring-2 ring-[#EDE8FF]' : 'border-[#EFF0F6] bg-white hover:border-[#C7D2FE]'}`}
                    >
                      <span className="block font-semibold text-[#1A1B2E]">{o.titre}</span>
                      <span className="block text-[11px] text-[#8E8FA5]">{o.aide}</span>
                    </button>
                  );
                })}
              </div>
              {values.lieu_defaut ? (
                <p className="text-[11px] text-[#8E8FA5]">
                  {values.lieu_defaut === 'NOS_LOCAUX'
                    ? "L'adresse vient de Paramètres → l'organisme. La corriger là la corrige partout."
                    : 'Le document prendra l’adresse du client quand il en connaît un.'}
                </p>
              ) : (
                <AddressAutocomplete
                  testIdPrefix="prog-address"
                  value={{ street: values.street, postal_code: values.postal_code, city: values.city }}
                  onChange={(next) => setValues((s) => ({ ...s, ...next }))}
                />
              )}
            </section>

            {/* Pédagogie */}
            <section className="space-y-3">
              <h3 className="text-[13px] font-semibold text-[#1A1B2E] uppercase tracking-wide">Pédagogie</h3>
              <Field label="Description" full>
                <textarea rows={3} value={values.description} onChange={(e) => setVal('description', e.target.value)} className={textareaCls} />
              </Field>
              <Field label="Objectifs pédagogiques" full>
                <textarea rows={3} value={values.objectives} onChange={(e) => setVal('objectives', e.target.value)} className={textareaCls} placeholder="Un objectif par ligne…" />
              </Field>
              <Field label="Prérequis" full>
                <textarea rows={2} value={values.prerequisites} onChange={(e) => setVal('prerequisites', e.target.value)} className={textareaCls} />
              </Field>
              <Field label="Programme détaillé" full>
                <Sequences
                  liste={values.chapters || []}
                  onChange={(v) => setVal('chapters', v)}
                />
              </Field>
              <Field label="Méthodes pédagogiques" full>
                <textarea
                  rows={3}
                  value={values.pedagogical_methods || ''}
                  onChange={(e) => setVal('pedagogical_methods', e.target.value)}
                  className={textareaCls}
                  placeholder="Une méthode par ligne — exposé théorique, mise en pratique en contexte professionnel…"
                />
                <Manque champ="pedagogical_methods" values={values} communes={communes} />
              </Field>
              <Field label="Modalités d'évaluation" full>
                <textarea
                  rows={2}
                  value={values.evaluation_methods || ''}
                  onChange={(e) => setVal('evaluation_methods', e.target.value)}
                  className={textareaCls}
                  placeholder="Ex. QCM final de 10 questions · mise en situation observée"
                />
                <Manque champ="evaluation_methods" values={values} communes={communes} />
              </Field>
              <Field label="Validation des acquis" full>
                <ValidationAcquis
                  mode={values.validation_acquis || ''}
                  criteres={values.criteres_acquis || []}
                  onMode={(v) => setVal('validation_acquis', v)}
                  onCriteres={(v) => setVal('criteres_acquis', v)}
                />
              </Field>
              <Field label="Ce qui est délivré" full>
                <Suggere
                  nom="sanction"
                  value={values.sanction}
                  options={suggestions.sanction}
                  onChange={(v) => setVal('sanction', v)}
                  placeholder={communes.sanction || "Attestation de fin de formation, certificat SST…"}
                />
                <Manque champ="sanction" values={values} communes={communes} />
              </Field>
              <Field label="Validité et recyclage" full>
                <Suggere
                  nom="validite"
                  value={values.validite}
                  options={suggestions.validite}
                  onChange={(v) => setVal('validite', v)}
                  placeholder="Recyclage tous les 2 ans maximum"
                />
                <Aide>Ce que le client doit savoir avant de signer : jusqu&rsquo;à quand ça vaut.</Aide>
              </Field>
              <Field label="PDF du programme (URL)" full>
                <input type="url" value={values.programme_pdf_url || ''} onChange={(e) => setVal('programme_pdf_url', e.target.value)} placeholder="https://… (lien public)" className={inputCls} />
              </Field>
              <Field label="PDF du programme (upload)" full>
                <ProgrammePdfUpload
                  programId={isEdit ? initialValues?.id : null}
                  initialFilename={values.uploaded_pdf_filename}
                  initialSize={values.uploaded_pdf_size}
                  onUploaded={(meta) => setValues((s) => ({ ...s, uploaded_pdf_filename: meta.filename, uploaded_pdf_size: meta.size, uploaded_pdf_at: meta.uploaded_at }))}
                  onDeleted={() => setValues((s) => ({ ...s, uploaded_pdf_filename: undefined, uploaded_pdf_size: undefined, uploaded_pdf_at: undefined }))}
                />
              </Field>
            </section>

            {/* Conditions particulières — la forme du site de la maison, et
                l'ordre dans lequel la fiche PDF les imprime. */}
            <section className="space-y-3">
              <h3 className="text-[13px] font-semibold text-[#1A1B2E] uppercase tracking-wide">Conditions particulières</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <Field label="Participants au minimum">
                  <input
                    type="number" min={0} data-testid="prog-effectif-min"
                    value={values.effectif_min || 0}
                    onChange={(e) => setVal('effectif_min', Number(e.target.value))}
                    className={inputCls}
                  />
                </Field>
                <Field label="Participants au maximum">
                  <input
                    type="number" min={0} data-testid="prog-effectif-max"
                    value={values.effectif_max || 0}
                    onChange={(e) => setVal('effectif_max', Number(e.target.value))}
                    className={inputCls}
                  />
                  <Manque champ="effectif_max" values={values} communes={communes} />
                </Field>
                <Field label="Délai d'accès">
                  <Suggere
                    nom="delai_acces"
                    value={values.delai_acces}
                    options={suggestions.delai_acces}
                    onChange={(v) => setVal('delai_acces', v)}
                    placeholder={communes.delai_acces || "15 jours à 1 mois"}
                  />
                  <Manque champ="delai_acces" values={values} communes={communes} />
                </Field>
                <Field label="Qualification du formateur">
                  <Suggere
                    nom="qualification_formateur"
                    value={values.qualification_formateur}
                    options={suggestions.qualification_formateur}
                    onChange={(v) => setVal('qualification_formateur', v)}
                    placeholder={communes.qualification_formateur || "Formateur SST à jour de son MAC"}
                  />
                  <Manque champ="qualification_formateur" values={values} communes={communes} />
                </Field>
              </div>
              <Field label="Moyens et matériel" full>
                <textarea
                  rows={2}
                  value={values.moyens_pedagogiques || ''}
                  onChange={(e) => setVal('moyens_pedagogiques', e.target.value)}
                  className={textareaCls}
                  placeholder="Mannequins, défibrillateur de formation, support visuel, aide-mémoire remis…"
                />
                <Manque champ="moyens_pedagogiques" values={values} communes={communes} />
              </Field>
              <Field label="Textes officiels de référence" full>
                <Suggere
                  nom="textes_officiels"
                  value={values.textes_officiels}
                  options={suggestions.textes_officiels}
                  onChange={(v) => setVal('textes_officiels', v)}
                  placeholder="Documents de référence de l'INRS en vigueur"
                />
                <Aide>Seulement pour une formation réglementée — sinon, laissez vide.</Aide>
              </Field>
              <Field label="Accessibilité aux personnes en situation de handicap" full>
                <textarea
                  rows={2}
                  value={values.accessibility || ''}
                  onChange={(e) => setVal('accessibility', e.target.value)}
                  className={textareaCls}
                  placeholder="Laissez vide pour la phrase de l'organisme — à remplir si cette formation demande des adaptations particulières."
                />
                <Manque champ="accessibility" values={values} communes={communes} />
              </Field>
            </section>

            {/* Note moyenne (lecture seule — calculée depuis évaluations) */}
            {isEdit && (
              <section className="rounded-xl border border-[#C7D2FE] bg-[#EEF2FF]/30 p-4 flex items-center gap-3">
                <Star className="w-5 h-5 text-[#F59E0B] fill-[#F59E0B]" />
                <div className="flex-1">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E]">Note moyenne (automatique)</div>
                  <div className="text-[11.5px] text-[#6B6C85]">Calculée à partir des évaluations apprenants rattachées aux sessions de ce programme.</div>
                </div>
                <div className="text-[18px] font-bold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {realRating?.count > 0 ? `${realRating.avg} / 5` : '—'}
                </div>
                <div className="text-[11px] font-semibold text-[#4F46E5] bg-white rounded-full px-2 py-0.5 border border-[#C7D2FE]">
                  {realRating?.count > 0 ? `${realRating.count} éval.` : 'Aucune éval.'}
                </div>
              </section>
            )}

            {error && (
              <div className="text-[12px] text-[#B91C1C] bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-3 py-2">{error}</div>
            )}
            <DialogFooter className="pt-2">
              <button type="button" onClick={() => onOpenChange(false)} className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] hover:border-[#C7D2FE]">Annuler</button>
              <button type="submit" data-testid="prog-submit-btn" disabled={loading} className="h-10 px-5 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer le programme'}
              </button>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "w-full h-10 px-3 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors";
const textareaCls = "w-full px-3 py-2 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors resize-none";

// Un champ qui propose sans imposer. Les valeurs viennent des defauts du
// serveur **puis de ce qui a deja ete tape sur les autres fiches** : taper
// « Initiale » sur quarante et un programmes donne sinon « Initiale »,
// « initiale » et « Initial ». Ce qui n'est pas dans la liste se tape quand
// meme — c'est un cadrage, pas une cloison.
function Suggere({ nom, value, options, onChange, placeholder }) {
  const liste = `suggestions-${nom}`;
  const valeurs = options || [];
  return (
    <>
      <input
        list={valeurs.length ? liste : undefined}
        data-testid={`prog-field-${nom}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
      />
      {valeurs.length > 0 && (
        <datalist id={liste}>
          {valeurs.map((v) => <option key={v} value={v} />)}
        </datalist>
      )}
    </>
  );
}

function Aide({ children }) {
  return <div className="text-[11px] text-[#8E8FA5] mt-1">{children}</div>;
}

// Ce que Qualiopi attend et que cette fiche ne dit pas encore. On n'empeche
// jamais d'enregistrer : on le dit la ou ca se corrige, et la phrase dit
// pourquoi — un champ qui manque sans dire pourquoi est un champ qu'on laisse
// vide. Une fois rempli, le reproche disparait sous les doigts.
const CLE_COMMUNE = {
  pedagogical_methods: 'methods',
  evaluation_methods: 'evaluation',
  accessibility: 'accessibility',
};

function Manque({ champ, values, communes }) {
  const valeur = values?.[champ];
  const vide = typeof valeur === 'number' ? !valeur : !String(valeur || '').trim();
  if (!vide) return <Aide>{ATTENDUS[champ]}</Aide>;
  // Vide mais dit par l'organisme : ce n'est pas un manque, c'est ce qui
  // s'imprimera. On le montre, parce qu'un champ vide ne dit pas ce qu'il
  // vaut.
  const commune = String(communes?.[CLE_COMMUNE[champ] || champ] || '').trim();
  if (commune) {
    return (
      <div data-testid={`prog-commune-${champ}`} className="text-[11px] text-[#6B6C85] mt-1">
        Vide : c&rsquo;est ce que dit votre organisme qui s&rsquo;imprimera —
        <span className="text-[#4A4B63]"> « {commune.length > 90 ? `${commune.slice(0, 90)}…` : commune} »</span>
      </div>
    );
  }
  return (
    <div data-testid={`prog-manque-${champ}`} className="text-[11px] text-[#B45309] mt-1">
      {ATTENDUS[champ]}
    </div>
  );
}

function Field({ label, required, full, children }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-[12px] font-semibold text-[#4A4B63] block mb-1.5">{label}{required && <span className="text-[#EF4444] ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}


function ProgrammePdfUpload({ programId, initialFilename, initialSize, onUploaded, onDeleted }) {
  const [filename, setFilename] = useState(initialFilename || '');
  const [size, setSize] = useState(initialSize || 0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    setFilename(initialFilename || '');
    setSize(initialSize || 0);
  }, [initialFilename, initialSize]);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!programId) {
      setError("Enregistrez d'abord le programme avant d'uploader un PDF.");
      e.target.value = '';
      return;
    }
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Le fichier doit être un PDF.');
      e.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('Le fichier dépasse 8 Mo.');
      e.target.value = '';
      return;
    }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/programs/${programId}/upload-pdf`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFilename(data.filename);
      setSize(data.size);
      onUploaded && onUploaded(data);
      toast({ title: 'PDF uploadé', description: `${data.filename} (${(data.size / 1024).toFixed(0)} Ko)` });
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload impossible.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!programId || !filename) return;
    if (!window.confirm(`Supprimer le PDF "${filename}" ?`)) return;
    try {
      await api.delete(`/programs/${programId}/upload-pdf`);
      setFilename('');
      setSize(0);
      onDeleted && onDeleted();
      toast({ title: 'PDF supprimé' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Suppression impossible.');
    }
  };

  const handleView = async () => {
    if (!programId) return;
    try {
      const res = await api.get(`/programs/${programId}/uploaded-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setError("Impossible d'ouvrir le PDF.");
    }
  };

  if (!programId) {
    return <div className="text-[11.5px] text-[#B45309] bg-[#FEF3E7] border border-[#FCD9A8] rounded-lg px-3 py-2">Enregistrez le programme pour activer l'upload.</div>;
  }

  return (
    <div className="space-y-2">
      {filename ? (
        <div className="flex items-center gap-2 rounded-xl border border-[#A7F3D0] bg-[#E6FBF7] px-3 py-2">
          <FileText className="w-4 h-4 text-[#0F766E] shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-[#0F766E] truncate" title={filename}>{filename}</div>
            <div className="text-[11px] text-[#0F766E]/80">{(size / 1024).toFixed(0)} Ko</div>
          </div>
          <button
            type="button"
            data-testid="prog-pdf-view"
            onClick={handleView}
            className="text-[12px] font-semibold text-[#4F46E5] hover:underline"
          >Voir</button>
          <button
            type="button"
            data-testid="prog-pdf-replace"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="text-[12px] font-semibold text-[#4F46E5] hover:underline disabled:opacity-60"
          >{uploading ? 'Upload…' : 'Remplacer'}</button>
          <button
            type="button"
            data-testid="prog-pdf-delete"
            onClick={handleDelete}
            className="w-7 h-7 rounded-lg hover:bg-white text-[#EF4444] flex items-center justify-center"
            title="Supprimer le PDF"
          ><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ) : (
        <button
          type="button"
          data-testid="prog-pdf-upload-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full h-10 px-3 rounded-xl bg-[#EEF2FF] hover:bg-[#EDE8FF] border border-dashed border-[#C7B7FA] text-[13px] font-semibold text-[#4F46E5] flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
          {uploading ? 'Upload en cours…' : 'Uploader un PDF de programme'}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleSelect}
        className="hidden"
        data-testid="prog-pdf-file-input"
      />
      {error && <div className="text-[11.5px] text-[#B91C1C]">{error}</div>}
      <div className="text-[11px] text-[#8E8FA5]">PDF — 8 Mo max. Stocké de manière chiffrée et lié à ce programme.</div>
    </div>
  );
}


// Comment un programme valide ses acquis, et contre quels critères.
//
// Huit programmes sur quarante-et-un ne se valident pas par un QCM : on ne
// coche pas un massage cardiaque. Les trente-trois autres — Microsoft, Sage,
// WordPress — n'ont rien à déclarer, et c'est le défaut : laissé vide, le
// quiz décide s'il y en a un.
//
// **Trois à six critères.** Une grille de vingt lignes par participant, un
// vendredi à 17 h, se remplit machinalement — et une case que tout le monde
// coche par défaut ne prouve rien.
/* Le déroulé, séquence par séquence.
   **C'est la rubrique qu'un auditeur lit en premier**, et la seule du document
   qui n'avait aucun champ : la section existait côté PDF, rien ne l'écrivait.
   Le contenu réel vit aujourd'hui dans la description — mesure sur les
   quarante-deux fiches : **trente et une sont un seul bloc de prose**, et parmi
   les sept qui portent trois lignes ou plus, la moitié sont des paragraphes
   commerciaux coupés au hasard de la largeur. Un découpage automatique se
   tromperait plus souvent qu'il n'aiderait : c'est une saisie, et le document
   n'imprime rien tant qu'elle est vide. */
function Sequences({ liste, onChange }) {
  const l = Array.isArray(liste) ? liste : [];
  const titreDe = (s) => (typeof s === 'string' ? s : (s?.title || s?.name || ''));
  const detailDe = (s) => (typeof s === 'string' ? '' : (s?.description || s?.content || ''));

  const poser = (i, champ, texte) => onChange(l.map((s, idx) => (idx === i
    ? { title: titreDe(s), description: detailDe(s), [champ]: texte }
    : s)));
  const ajouter = () => onChange([...l, { title: '', description: '' }]);
  const retirer = (i) => onChange(l.filter((_, idx) => idx !== i));
  const deplacer = (i, pas) => {
    const j = i + pas;
    if (j < 0 || j >= l.length) return;
    const copie = [...l];
    [copie[i], copie[j]] = [copie[j], copie[i]];
    onChange(copie);
  };

  return (
    <div className="rounded-xl border border-dashed border-[#DDD6FE] bg-[#FAF8FF] p-3">
      {l.length === 0 && (
        <div className="text-[11.5px] text-[#B45309] mb-2">
          Aucune séquence : le document n&rsquo;imprimera pas de programme détaillé.
        </div>
      )}
      <div className="space-y-2">
        {l.map((s, i) => (
          <div key={i} className="rounded-lg bg-white border border-[#EFF0F6] p-2">
            <div className="flex items-center gap-2">
              <span className="w-6 text-[11px] font-semibold text-[#7C5CFC] tabular-nums">
                {String(i + 1).padStart(2, '0')}
              </span>
              <input
                value={titreDe(s)}
                onChange={(e) => poser(i, 'title', e.target.value)}
                data-testid={`sequence-titre-${i}`}
                placeholder="Ex. Séquence 2 — protéger : reconnaître le danger, dégager d'urgence"
                className="flex-1 h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]"
              />
              <button
                type="button" onClick={() => deplacer(i, -1)} disabled={i === 0}
                title="Monter cette séquence"
                className="w-7 h-8 rounded-lg text-[#6B6C85] hover:bg-[#F6F7FB] disabled:opacity-30"
              >
                ↑
              </button>
              <button
                type="button" onClick={() => deplacer(i, 1)} disabled={i === l.length - 1}
                title="Descendre cette séquence"
                className="w-7 h-8 rounded-lg text-[#6B6C85] hover:bg-[#F6F7FB] disabled:opacity-30"
              >
                ↓
              </button>
              <button
                type="button" onClick={() => retirer(i)}
                title="Retirer cette séquence"
                className="w-8 h-8 rounded-lg text-[#EF4444] hover:bg-[#F6F7FB]"
              >
                ×
              </button>
            </div>
            <textarea
              rows={2}
              value={detailDe(s)}
              onChange={(e) => poser(i, 'description', e.target.value)}
              data-testid={`sequence-detail-${i}`}
              placeholder="Le détail, facultatif — ce qu'on y fait, les points abordés."
              className="mt-1.5 w-full px-2 py-1.5 rounded-lg border border-[#EFF0F6] bg-white text-[12px] text-[#4A4B63]"
            />
          </div>
        ))}
      </div>
      <button
        type="button" onClick={ajouter} data-testid="ajouter-sequence"
        className="mt-2 h-8 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12px] font-semibold text-[#6D28D9] hover:bg-[#F3EFFF]"
      >
        + Ajouter une séquence
      </button>
      <div className="text-[11px] text-[#8E8FA5] mt-2">
        Le titre suffit ; le détail est facultatif. C&rsquo;est ce que le document
        imprime sous <strong>Programme détaillé</strong>, numéroté dans cet ordre.
      </div>
    </div>
  );
}

function ValidationAcquis({ mode, criteres, onMode, onCriteres }) {
  const liste = Array.isArray(criteres) ? criteres : [];
  const libelleDe = (c) => (typeof c === 'string' ? c : (c?.libelle || ''));
  const avecGrille = mode === 'grille' || mode === 'les_deux';

  const poser = (i, texte) => {
    const copie = liste.map((c, idx) => (idx === i
      ? { id: (typeof c === 'object' && c?.id) || `c${i + 1}`, libelle: texte }
      : c));
    onCriteres(copie);
  };
  const ajouter = () => onCriteres([...liste, { id: `c${liste.length + 1}`, libelle: '' }]);
  const retirer = (i) => onCriteres(liste.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {[
          ['', 'Non déclaré'],
          ['qcm', 'Par le questionnaire'],
          ['grille', 'Par la grille du formateur'],
          ['les_deux', 'Les deux'],
        ].map(([v, lbl]) => (
          <button
            key={v || 'vide'} type="button"
            data-testid={`validation-acquis-${v || 'vide'}`}
            onClick={() => onMode(v)}
            className={`h-8 px-3 rounded-lg text-[12px] font-semibold border transition-colors ${
              (mode || '') === v
                ? 'bg-[#EEF2FF] text-[#4F46E5] border-[#C7D2FE]'
                : 'bg-white text-[#6B6C85] border-[#EFF0F6] hover:bg-[#F6F7FB]'
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>

      {/* **Plus d'arbitre** (2026-11-02, « si les 2 on prend en compte les
          2 »). Un menu demandait lequel tranche en cas de desaccord ; les deux
          comptent desormais, donc il ne commandait plus rien. */}
      {mode === 'les_deux' && (
        <div className="text-[12px] text-[#4A4B63]">
          Les deux comptent : il faut <strong>70 %</strong> au questionnaire
          <strong> et</strong> 70 % des critères acquis. Un seul résultat
          négatif ne valide pas ; tant qu&rsquo;il en manque un, rien n&rsquo;est
          tranché.
        </div>
      )}

      {avecGrille && (
        <div className="rounded-xl border border-dashed border-[#A7F3E4] bg-[#F4FDFB] p-3">
          <div className="text-[11.5px] font-semibold text-[#0F766E] mb-2">
            Les critères observés — trois à six
          </div>
          {liste.length === 0 && (
            <div className="text-[11.5px] text-[#B45309] mb-2">
              Aucun critère : la grille ne sera proposée à personne.
            </div>
          )}
          <div className="space-y-1.5">
            {liste.map((c, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={libelleDe(c)}
                  onChange={(e) => poser(i, e.target.value)}
                  data-testid={`critere-${i}`}
                  placeholder="Ex. Réalise le dégagement d'urgence"
                  className="flex-1 h-8 px-2 rounded-lg border border-[#EFF0F6] bg-white text-[12.5px]"
                />
                <button
                  type="button" onClick={() => retirer(i)}
                  title="Retirer ce critère"
                  className="w-8 h-8 rounded-lg text-[#EF4444] hover:bg-white"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {liste.length < 6 && (
            <button
              type="button" onClick={ajouter} data-testid="ajouter-critere"
              className="mt-2 h-8 px-3 rounded-lg bg-white border border-[#EFF0F6] text-[12px] font-semibold text-[#0F766E] hover:bg-[#E6FBF7]"
            >
              + Ajouter un critère
            </button>
          )}
          <div className="text-[11px] text-[#8E8FA5] mt-2">
            Le formateur les coche depuis le lien d&rsquo;émargement, une fois la
            dernière séance signée. Acquis veut dire <strong>70 % des critères</strong>
            {/* En entiers, comme le serveur, qui compare `acquis * 100 >= 70 * n`.
                Verifie de 3 a 20 criteres : les deux donnent le meme chiffre.
                `n * 0.7` aurait donne le meme aussi — 0,7 en double est un
                cheveu sous 7/10, donc le produit retombe juste — mais on ne
                fait pas dependre un seuil d'un arrondi heureux. */}
            {liste.length > 0 && <> — soit {Math.ceil(liste.length * 70 / 100)} sur {liste.length}</>}.
            « En cours d&rsquo;acquisition » ne compte pas comme acquis.
          </div>
        </div>
      )}
    </div>
  );
}
